import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AdminAuthContext";

export const AdminLoginPage = () => {
  const { login, me, ready } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from || "/admin";

  if (ready && me && me.ok) return <Navigate to={from} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back");
      nav(from, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-bone flex items-center justify-center px-6">
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.7, 0, 0.2, 1] }}
        onSubmit={onSubmit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-white border-hairline p-8 md:p-10"
      >
        <div className="flex items-baseline gap-2 mb-8">
          <span className="font-display text-3xl text-forest leading-none">Anabiya</span>
          <span className="font-display italic text-xl text-gold leading-none">Decor</span>
          <span className="ml-2 overline text-forest/60 border-l border-hairline pl-2">Admin</span>
        </div>

        <div className="overline text-gold mb-3">§ Sign in</div>
        <h1 className="font-display text-forest text-4xl leading-tight tracking-tight mb-8">
          The <span className="font-editorial italic text-champagne">house</span> is yours.
        </h1>

        <label className="overline text-forest/60">Email</label>
        <input
          data-testid="admin-login-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full bg-transparent border-b border-hairline focus:border-forest outline-none py-3 mb-6 text-forest transition-colors duration-500"
          autoComplete="email"
        />
        <label className="overline text-forest/60">Password</label>
        <input
          data-testid="admin-login-password"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-transparent border-b border-hairline focus:border-forest outline-none py-3 mb-8 text-forest transition-colors duration-500"
          autoComplete="current-password"
        />
        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 bg-forest text-white overline px-5 py-4 rounded-full disabled:opacity-50 hover:bg-[hsl(150_60%_16%)] transition-colors duration-500"
        >
          <Lock size={13} strokeWidth={1.6} />
          {busy ? "Signing in…" : "Enter"}
        </button>
        <p className="mt-6 text-center text-forest/50 text-xs">
          Ping IT if you&rsquo;ve forgotten your credentials.
        </p>
      </motion.form>
    </main>
  );
};

export const RequireAdmin = ({ children }) => {
  const { me, ready } = useAdminAuth();
  const loc = useLocation();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bone">
        <div className="font-display italic text-forest/60">Checking session…</div>
      </div>
    );
  }
  if (!me || !me.ok) return <Navigate to="/admin/login" state={{ from: loc.pathname }} replace />;
  return children;
};
